<modal-panel
  [title]="(isEditing ? 'Edit Manual ERP Update' : 'Add Manual ERP Update') + ' — Invoice ' + data.adxInvoiceId + (data.adxReference ? ' (' + data.adxReference + ')' : '')"
  data-testid="adx-erp-manual-update-dialog"
  (close)="cancel()">

  <p class="manual-update__hint">
    Use this when the invoice was sent to ERP outside FMS (e.g. sent manually because ERP outbound
    isn't enabled in this environment). Fill in the sales order number and timestamp you got from
    ERP so FMS closes out this invoice and captures the next edit as a fresh delta.
  </p>

  @if (isEditing) {
    <p class="manual-update__hint">
      <strong>Editing an existing manual entry.</strong> Made a mistake last time? Fix the fields
      below and save — the previous entry is replaced, not duplicated. (Result defaulted to ERROR
      only if an error description was previously recorded — the report grid doesn't expose the
      exact prior Result value, so double-check it's still correct.)
    </p>
  }

  <mat-expansion-panel>
    <mat-expansion-panel-header>
      <mat-panel-title>How this works &amp; what each field affects</mat-panel-title>
    </mat-expansion-panel-header>
    <ul class="manual-update__info">
      <li>
        <strong>Saving does two things, in order:</strong> (1) stamps this invoice's ErpMessageId
        / timestamp — this is the part that actually resets delta tracking, since AdxInvoice only
        tracks "sent" as a single ErpMessageId being non-null; any future edit to this invoice
        clears it back to null automatically, which is what makes that next edit look like a fresh
        delta again; (2) writes an ERP response record against this invoice, purely for
        reporting/timeline visibility. If step 1 succeeds but step 2 fails (or vice versa),
        re-open this dialog and save again — check the report grid afterward either way.
      </li>
      <li>
        <strong>ERP Sales Order Number</strong> — the real D365 sales order number ERP assigned.
        Only used for reporting/search (no delta-logic impact), but it's the actual proof a real
        order exists in ERP, so put the genuine number, not a placeholder.
      </li>
      <li>
        <strong>Sent to ERP (timestamp)</strong> — stamped on both the invoice and the response;
        this is the delta checkpoint FMS compares future changes against, and also drives
        date-filtered reports. An inaccurate timestamp won't break the delta reset itself, but will
        misplace this entry in date-filtered reports/timelines.
      </li>
      <li>
        <strong>Result</strong> — mirrors what a real ERP response would report (SUCCESS / PENDING
        / ERROR). Note the record's internal ApplyStatus is always set to "Pending" regardless of
        what you choose here — that's a separate internal processing flag, not tied to this Result
        value.
      </li>
      <li>
        <strong>ERP Message Id</strong> — an internal FMS correlation id, not the sales order
        number. It also decides which row gets written: if it matches an existing response row
        that's still "PENDING", saving overwrites that row instead of creating a new one — leave
        the auto-generated value unless you deliberately mean to update a specific existing
        record.
      </li>
      <li>
        <strong>Editing:</strong> reopening this dialog on an invoice that already has a response
        preloads its values and reuses the same ERP Message Id. Saving deletes the old response
        row first, then writes a fresh one — so a correction always fully replaces the previous
        entry instead of leaving a stale duplicate behind, no matter what Result it had.
      </li>
    </ul>
  </mat-expansion-panel>

  @if (data.alreadySynced) {
    <p class="manual-update__warning">
      This invoice already has an ERP message recorded — it may already be closed out (e.g. by the
      real outbound pipeline, or a previous manual update). Saving again will overwrite that
      record with the values below.
    </p>
  }

  <div class="manual-update__fields" [formGroup]="form">
    <div class="manual-update__field">
      <mat-form-field appearance="outline">
        <mat-label>ERP Sales Order Number</mat-label>
        <input matInput formControlName="erpOrderNumber" [maxlength]="erpOrderNumberMaxLength" data-testid="adx-erp-manual-update-order-number" />
        @if (form.get('erpOrderNumber')?.hasError('maxlength')) {
          <mat-hint>Max {{ erpOrderNumberMaxLength }} characters.</mat-hint>
        }
      </mat-form-field>
      <p class="manual-update__field-note">
        The D365 sales order number shown after you manually process the send in D365. This is
        the actual proof a real order exists in ERP — enter the genuine number, not a
        placeholder.
      </p>
    </div>

    <div class="manual-update__field">
      <mat-form-field appearance="outline">
        <mat-label>Sent to ERP</mat-label>
        <input matInput type="datetime-local" formControlName="erpMessageTimestamp" [max]="nowInputValue" data-testid="adx-erp-manual-update-timestamp" />
        @if (form.get('erpMessageTimestamp')?.hasError('futureDate')) {
          <mat-hint>This timestamp is in the future.</mat-hint>
        }
      </mat-form-field>
      <p class="manual-update__field-note">
        The date/time D365 shows the sales order was actually created/processed. Cannot be in the
        future — this is the delta checkpoint FMS compares future edits against and also drives
        date-filtered reports, so enter the real value, not just "now".
        @if (isEditing) {
          The report grid can't reliably show you the value entered last time (it displays the
          response record's save time instead), so re-enter the real ERP date/time here.
        }
      </p>
    </div>

    <div class="manual-update__field">
      <mat-form-field appearance="outline">
        <mat-label>Result</mat-label>
        <mat-select formControlName="result" data-testid="adx-erp-manual-update-result">
          @for (option of resultOptions; track option) {
            <mat-option [value]="option">{{ option }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    @if (form.get('result')?.value === 'ERROR') {
      <div class="manual-update__field">
        <mat-form-field appearance="outline">
          <mat-label>Error Description</mat-label>
          <textarea matInput formControlName="errorDescription" rows="3" data-testid="adx-erp-manual-update-error-description"></textarea>
        </mat-form-field>
        <p class="manual-update__field-note">Required when Result is ERROR.</p>
      </div>
    }

    <div class="manual-update__field">
      <mat-form-field appearance="outline">
        <mat-label>ERP Message Id</mat-label>
        <input matInput formControlName="erpMessageId" [readonly]="isEditing" data-testid="adx-erp-manual-update-message-id" />
        @if (form.get('erpMessageId')?.hasError('invalidGuid')) {
          <mat-hint>Must be a valid GUID.</mat-hint>
        }
      </mat-form-field>
      <p class="manual-update__field-note">
        @if (isEditing) {
          Reused from the entry being edited so saving replaces it rather than creating a
          duplicate. Only change this if you deliberately want to correlate to a different
          in-flight Service Bus message.
        } @else {
          Not shown in D365/ADX screens — if correlating to a specific in-flight message, copy
          the MessageId from the Azure Portal's Service Bus Explorer for the ERP outbound
          queue/topic. Otherwise leave the auto-generated value.
        }
      </p>
      @if (!isEditing) {
        <button mat-button type="button" (click)="regenerateMessageId()">Regenerate Id</button>
      }
    </div>
  </div>

  <ng-container modalPanelFooter>
    <button
      mat-raised-button
      color="primary"
      type="submit"
      data-testid="adx-erp-manual-update-submit-button"
      (click)="submit()"
      [disabled]="saving || form.invalid">
      @if (saving) {
        <mat-spinner diameter="20"/>
      } @else {
        Save
      }
    </button>
    <button mat-button (click)="cancel()">Cancel</button>
  </ng-container>
</modal-panel>
